import React, { useEffect, useState } from "react";
import { db } from "../../../firebase";
import { collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { getIconoRubro } from "../../../utils/iconosRubros";

export default function ListadoRubrosAdmin() {
  const [rubros, setRubros] = useState([]);
  const [nuevoRubro, setNuevoRubro] = useState('');
  const [rubroEditando, setRubroEditando] = useState(null);
  const [nombreEditando, setNombreEditando] = useState('');
  const [loading, setLoading] = useState(false);

  // Rubros más comunes del mundo automotriz
  const rubrosSugeridos = [
    'Vulcanización', 'Lavado', 'Talleres mecánicos', 'Pintura', 'Lubricentro',
    'Accesorios', 'Grúas', 'Motos', 'Repuestos', 'Neumáticos', 'Baterías',
    'Frenos', 'Eléctrico', 'Carrocería', 'Escapes', 'Filtros', 'Herramientas',
    'Diagnóstico', 'Mantención', 'Mecánica General', 'Audio y Alarmas',
    'Climatización', 'Transmisión', 'Suspensión', 'Desarmaduría',
    'Electricidad automotriz', 'Enderezado y pintura', 'Hojalatería', 'Tapicería',
    'Cristales', 'Alineación y balanceo', 'Inyección diesel', 'Inyección bencinera',
    'Sistema de escape', 'Dirección hidráulica', 'Embrague', 'Frenos ABS',
    'Llantas y rines', 'GNC y conversión', 'Automotoras', 'Peritaje',
    'Patentamiento', 'Revisión técnica', 'Seguros vehiculares', 'Grúas y remolques',
    'Estacionamientos', 'Lava autos', 'Detallado', 'Polimento', 'Ploteo',
    'Tunning', 'Retrofit eléctrico', 'Reciclaje automotriz', 'Partes usadas',
    'Importación de vehículos', 'Venta de combustibles', 'Rent a car',
    'Aire acondicionado', 'Sistema de enfriamiento', 'Distribución',
    'Caja de cambios', 'Diferencial', 'Amortiguadores', 'Otros'
  ];

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "rubros_empresa"), (snap) => {
      setRubros(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, []);

  const agregarRubro = async (nombre) => {
    if (!nombre.trim()) return;

    const nombreNormalizado = nombre.trim().toLowerCase();
    const existe = rubros.some(r => r.nombre.toLowerCase() === nombreNormalizado);

    if (existe) {
      alert('Este rubro ya existe. No se pueden agregar rubros duplicados.');
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, 'rubros_empresa'), {
        nombre: nombre.trim(),
        fechaCreacion: new Date(),
        activa: true
      });
      setNuevoRubro('');
      console.log('✅ Rubro agregado:', nombre);
    } catch (error) {
      console.error('❌ Error agregando rubro:', error);
      alert('Error al agregar el rubro');
    } finally {
      setLoading(false);
    }
  };

  const editarRubro = async (id, nuevoNombre) => {
    if (!nuevoNombre.trim()) return;

    const nombreNormalizado = nuevoNombre.trim().toLowerCase();
    const existe = rubros.some(r =>
      r.nombre.toLowerCase() === nombreNormalizado && r.id !== id
    );

    if (existe) {
      alert('Ya existe un rubro con este nombre.');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'rubros_empresa', id), {
        nombre: nuevoNombre.trim(),
        fechaModificacion: new Date()
      });
      setRubroEditando(null);
      setNombreEditando('');
      console.log('✅ Rubro editado:', nuevoNombre);
    } catch (error) {
      console.error('❌ Error editando rubro:', error);
      alert('Error al editar el rubro');
    } finally {
      setLoading(false);
    }
  };

  const eliminarRubro = async (id, nombre) => {
    if (!confirm(`¿Estás seguro de eliminar el rubro "${nombre}"?`)) return;

    setLoading(true);
    try {
      await deleteDoc(doc(db, 'rubros_empresa', id));
      console.log('✅ Rubro eliminado:', nombre);
    } catch (error) {
      console.error('❌ Error eliminando rubro:', error);
      alert('Error al eliminar el rubro');
    } finally {
      setLoading(false);
    }
  };

  const agregarRubrosSugeridos = async () => {
    if (!confirm('¿Deseas agregar todos los rubros sugeridos que no existen aún?')) return;

    setLoading(true);
    const rubrosExistentes = rubros.map(r => r.nombre.toLowerCase());
    const rubrosNuevos = rubrosSugeridos.filter(
      rub => !rubrosExistentes.includes(rub.toLowerCase())
    );

    try {
      for (const rubro of rubrosNuevos) {
        await addDoc(collection(db, 'rubros_empresa'), {
          nombre: rubro,
          fechaCreacion: new Date(),
          activa: true
        });
      }
      console.log(`✅ ${rubrosNuevos.length} rubros agregados`);
      alert(`Se agregaron ${rubrosNuevos.length} rubros nuevos`);
    } catch (error) {
      console.error('❌ Error agregando rubros sugeridos:', error);
      alert('Error al agregar los rubros sugeridos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Gestión de Rubros Específicos</h2>
        <p className="text-gray-600">
          Administra los rubros disponibles para el formulario de registro de proveedores ({' '}
          <a href="/registro-empresa" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
            /registro-empresa
          </a>
          ). Incluye Desarmaduría y otros rubros del sector automotriz.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-600">{rubros.length}</div>
          <div className="text-sm text-blue-600">Total Rubros</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {rubros.filter(r => r.activa !== false).length}
          </div>
          <div className="text-sm text-green-600">Activos</div>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">
            {rubrosSugeridos.filter(
              rub => !rubros.some(r => r.nombre.toLowerCase() === rub.toLowerCase())
            ).length}
          </div>
          <div className="text-sm text-yellow-600">Sugeridos Pendientes</div>
        </div>
      </div>

      {/* Botón para cargar todos los rubros automotrices */}
      <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">🚗 Cargar rubros del sector automotriz</h3>
        <p className="text-sm text-gray-600 mb-3">
          Agrega a la colección los {rubrosSugeridos.length} rubros más comunes del mundo automotriz. Solo se agregan los que aún no existen.
        </p>
        <button
          onClick={agregarRubrosSugeridos}
          disabled={loading}
          className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
        >
          {loading ? 'Cargando...' : 'Cargar todos los rubros automotrices'}
        </button>
      </div>

      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">Agregar Nuevo Rubro</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={nuevoRubro}
            onChange={(e) => setNuevoRubro(e.target.value)}
            placeholder="Ej: Desarmaduría"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyPress={(e) => e.key === 'Enter' && agregarRubro(nuevoRubro)}
            disabled={loading}
          />
          <button
            onClick={() => agregarRubro(nuevoRubro)}
            disabled={loading || !nuevoRubro.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {loading ? 'Agregando...' : 'Agregar'}
          </button>
        </div>
      </div>

      <div className="mb-6 p-4 bg-yellow-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Rubros Sugeridos (incluye Desarmaduría)</h3>
          <button
            onClick={agregarRubrosSugeridos}
            disabled={loading}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 transition-colors text-sm"
          >
            {loading ? 'Agregando...' : 'Agregar Todos los Sugeridos'}
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {rubrosSugeridos.map(rubro => {
            const existe = rubros.some(r => r.nombre.toLowerCase() === rubro.toLowerCase());
            return (
              <span
                key={rubro}
                className={`px-3 py-1 rounded-full text-sm ${
                  existe
                    ? 'bg-green-100 text-green-800 line-through'
                    : 'bg-yellow-100 text-yellow-800 cursor-pointer hover:bg-yellow-200'
                }`}
                onClick={!existe ? () => agregarRubro(rubro) : undefined}
                title={existe ? 'Ya existe' : 'Clic para agregar'}
              >
                {rubro} {existe ? '✓' : '+'}
              </span>
            );
          })}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-200 rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Nombre</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Estado</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Fecha Creación</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rubros.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                  <div className="text-4xl mb-2">🏷️</div>
                  <div>No hay rubros registrados</div>
                  <div className="text-sm mt-2">Agrega rubros o usa &quot;Agregar Todos los Sugeridos&quot; para incluir Desarmaduría y el resto</div>
                </td>
              </tr>
            ) : (
              rubros.map((rubro) => (
                <tr key={rubro.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 border-b">
                    {rubroEditando === rubro.id ? (
                      <input
                        type="text"
                        value={nombreEditando}
                        onChange={(e) => setNombreEditando(e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') editarRubro(rubro.id, nombreEditando);
                          if (e.key === 'Escape') { setRubroEditando(null); setNombreEditando(''); }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="font-medium text-gray-900 inline-flex items-center gap-2">
                        <span className="text-xl">{getIconoRubro(rubro.nombre)}</span>
                        {rubro.nombre}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      rubro.activa !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {rubro.activa !== false ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 border-b">
                    {rubro.fechaCreacion?.toDate ? new Date(rubro.fechaCreacion.toDate()).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3 text-right border-b">
                    <div className="flex justify-end gap-2">
                      {rubroEditando === rubro.id ? (
                        <>
                          <button onClick={() => editarRubro(rubro.id, nombreEditando)} disabled={loading}
                            className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400 text-sm">Guardar</button>
                          <button onClick={() => { setRubroEditando(null); setNombreEditando(''); }}
                            className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm">Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => { setRubroEditando(rubro.id); setNombreEditando(rubro.nombre); }}
                            className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Editar</button>
                          <button onClick={() => eliminarRubro(rubro.id, rubro.nombre)} disabled={loading}
                            className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400 text-sm">Eliminar</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span>Procesando...</span>
          </div>
        </div>
      )}
    </div>
  );
}
